export class LiveChat2Wrapper {
  #version;
  #productName;
  #LiveChat2Api;
  #startCommandObj;
  #apiConfig = {};
  #apiFactory = {};
  #captchaApi;
  #avInterface;
  #userSentMessagesTemp = {};
  #internalFlags = {
    hasServerStateChangedOnce: false
  };
  constructor(startCommandObj, Livechat2Api, logger) {
    this.#startCommandObj = startCommandObj;
    this.#apiConfig = {
      captchaServiceUrl: this.#startCommandObj.captchaServiceUrl
    };

    this.#version = "1.0.0";
    this.#productName = "LiveChat2 Wrapper";    
    this.logger = logger;
    this.logger.info(`Initialized: ${this.#productName}, ${this.#version}`);
    console.log(`%c Initialized: ${this.#productName}: ${this.#version}`, 
        'color:white; background: purple;');
    this.#LiveChat2Api = new Livechat2Api(this.#startCommandObj.url, this.#apiConfig);
    this.#LiveChat2Api.sdkLoggingConfigurations(true, true, false);
    this.#apiFactory = this.#LiveChat2Api.api;
    this.#captchaApi = this.#apiFactory.captchaApi;
    this.#InitializeAvInterface();
    this.#LivechatManualOverrides();
    this.#LivechatApiManualOverrides();
  }

  #LivechatManualOverrides = () => {
    try {
      this.#LiveChat2Api.onCaptchaSuccess = (data) => {
        this.logger.info(`Captcha auth success ${JSON.stringify(data)}`);
        const userObject = {
          name: this.#startCommandObj.name,
          address: this.#startCommandObj.address,
          pIntent: this.#startCommandObj.pIntent
        };
        this.#LiveChat2Api.start(userObject);
      };

      this.#LiveChat2Api.onCaptchaFailed = (error) => {
        this.logger.info(`Captcha auth failed ${JSON.stringify(error)}`);
        const errorCodeMap = {
          '-1001': 'CaptchaNotFound',
          '-1002': 'CaptchaIsEmpty',
          '-1003': 'CaptchaDoesNotMatch'
        };

        this.#LiveChat2Api.onStartFailed({ code: errorCodeMap[error.errorCode] });
      };

      this.#LiveChat2Api.onStartSuccess = (sid, cnt) => {
        this.logger.info(`Session Start sid: ${sid}`);
        this.logger.info(`Session Start cnt: ${cnt}`);
        if (cnt != null && cnt.tchatid != null) {
          this.logger.info(`Chat Session Id ${JSON.stringify(cnt.tchatid)}`);
          this.OnStartSuccess(sid, cnt.tchatid);
        }
      };

      this.#LiveChat2Api.onStartFailed = (error) => {
        this.OnStartFailed(error);
        this.logger.info(`Start Failed ${JSON.stringify(error)}`);
      };

      this.#LiveChat2Api.onRequestQueued = (data) => {
        if (sessionStorage.getItem('Initial Load') <= 1) {
          //its a refresh so ignore call queued event
          this.OnCallQueued(data.position, data.queueType);
        }        
        this.logger.info(`Request queued ${JSON.stringify(data)}`);
      };

      this.#LiveChat2Api.onUserFound = (data) => {
        this.OnRemoteUserConnected(data.name, data.foundUser, data.sessionId, JSON.stringify({}));
        this.logger.info(`User found ${JSON.stringify(data)}`);
      };

      this.#LiveChat2Api.onUserLeft = (data) => {
        this.OnConferenceUserLeft(data.user, data.msg);
        this.logger.info(`User left ${JSON.stringify(data)}`);
      };

      this.#LiveChat2Api.getInteractionData = (data) => {
        const parsedContent = JSON.parse(data); // now we get { interactionData: ... }
        console.log('LiveChat2Api getInteractionData', parsedContent);
        const sessionData = {
          // sid_tchat: this.#LiveChat2Api.livechat.getSessionID(),
          sid_tchat: parsedContent.sid_tchat,
          sid_webapi: parsedContent.sid_webapi,
          trans: parsedContent.trans
        };
        this.OnOpenSuccess(sessionData);
      };

      this.#LiveChat2Api.onUserMessage = (data, from) => {
        const parsedMessage = JSON.parse(data);
        let isJsonMessageContent = false;
        try {
          JSON.parse(parsedMessage?.message_content);
          isJsonMessageContent = true;
        } catch (e) {
          isJsonMessageContent = false;
        }
        let convertedJsonMessage = {};
        if (parsedMessage?.attachment) {
          convertedJsonMessage = {
            attachment: parsedMessage.attachment,
            messageId: parsedMessage.mid,
            message: parsedMessage.message_content,
            type: parsedMessage.type,
            replyId: parsedMessage?.replyId,
            templateId: ''
          };
        } else if (isJsonMessageContent) {
          convertedJsonMessage = JSON.parse(parsedMessage.message_content);
        } else {
          convertedJsonMessage = {
            messageId: parsedMessage.mid,
            replyId: parsedMessage.replyId,
            type: parsedMessage.type,
            templateId: null,
            message: parsedMessage.message_content,
            attachment: null
          };
        }
        this.OnMessageReceived(from, JSON.stringify(convertedJsonMessage), parsedMessage.mid);
        this.logger.info(`Message received ${JSON.stringify(parsedMessage)} from ${from}`);
      };

      this.#LiveChat2Api.onTypingState = (data, from) => {
        const stateMapper = {
          STOP: 1,
          START: 0
        };
        this.OnTypingStateChanged(from, stateMapper[data.status]);
        this.logger.info(`Typing state changed ${JSON.stringify(data)} from ${from}`);
      };

      this.#LiveChat2Api.onSessionEnd = (data) => {
        this.OnEnd(data.initiator, data.reason);
        this.logger.info(`Session ended ${JSON.stringify(data)}`);
      };

      this.#LiveChat2Api.onEnd = (reason) => {
        this.OnEnd('client', reason);
        this.logger.info(`Session ended ${reason}`);
      };

      this.#LiveChat2Api.onSystemNotification = (notification) => {
        const reasonCode = String(notification.reasonCode).toLowerCase();
        switch (reasonCode) {
          case 'timedoutinqueue': {
            this.End(reasonCode);
            break;
          }
          default: {
            this.logger.info(`System notification ${reasonCode} support not implemented`);
          }
        }
        this.logger.info(`System notification ${reasonCode}`);
      };

      this.#LiveChat2Api.onServerStateChange = (state) => {
        // we do this check avoid the initial connect state
        if (!state || this.#internalFlags.hasServerStateChangedOnce) this.OnServerStateChanged(state);
        this.#internalFlags.hasServerStateChangedOnce = true;
        this.logger.info(`Server state changed ${state}`);
      };

      this.#LiveChat2Api.onSignatureRequest = ({content, from}) => {
        this.OnAppMessageReceived(from, content, from)
        this.logger.info(`Signature request received from ${from} with data ${content}`);
      }

      this.#LiveChat2Api.onActionMessage = ({content, from}) => {
        this.OnAppMessageReceived(from, content, from)
        this.logger.info(`Action message received from ${from} with data ${content}`);
      }
    } catch (ex) {
      this.logger.error(
        `[LiveChat2Wrapper.#LivechatManualOverrides] - Error occured while overriding livechat2 methods`,
        ex.message
      );
    }
  };

  #LivechatApiManualOverrides = () => {
    try {
      this.#LiveChat2Api.livechat.onDisconnect = (reason) => {
        // TODO: check this initiator part
        this.OnEnd('client', reason);
        this.logger.info(`Client side disconnect with reason ${reason}`);
      };
    } catch (ex) {
      this.logger.error(
        `[LiveChat2Wrapper.#LivechatManualOverrides] - Error occured while overriding livechat2 methods`,
        ex.message
      );
    }
  };

  SendAppMessage = (msg) => {
    try {
      const sid = this.#LiveChat2Api.livechat.getSessionID();
      this.#LiveChat2Api.sendAppEvent(msg, `interaction:${sid}`);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.SendAppMessage] - Error occured while sending app message`, ex.message);
    }
  };

  OnUserJoining = (agent, reason) => {};

  OnCallbackAutoRegistered = (reasonCode) => {};

  OnInternalServerError = (errorCode) => {};

  OnUploadComplete = (response) => {};

  prepareDownloadURL = (fileName) => {};

  OnAppMessageReceived = (agent, message, id) => {};

  OnServerStateChanged = (state) => {};

  OnEnd = (initiator, reason) => {};

  OnServersideEnd = (initiator, reason) => {};

  OnClientsideEnd = (initiator, reason) => {};

  OnConferenceUserLeft = (agent, reason) => {};

  OnTypingStateChanged = (agent, state) => {};

  OnRemoteUserConnected = (name, agentId, sessionID, extraParam) => {};

  OnMessageReceived = (agent, message, id) => {};

  End = (reason) => {
    try {
      this.#LiveChat2Api.end(reason);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.End] - Error occured while ending session`, ex.message);
    }
  };

  #setUserAuth = (enteredCaptchaValue) => {
    try {
      const userId = this.#GenerateRandomId(64);
      const authType = this.#startCommandObj.authType;
      const sessionId = this.#startCommandObj.pChatBotSid;
      // let authToken;
      //by adding this captcha is validating on start when captcha is enabled
      //without this it is not working when catcha is enabled
      let authToken = "eyJhbGciOiJIUzUxMiJ9.eyJjaGFubmVsIjoiVklWUiIsInNlc3Npb25JZCI6IjNhNjAwMzQyLWE3YTMtNGM2Ni1iYmQzLWY2N2RlNWQ3MDk2ZiIsImVudiI6IkRpY2VEZXYiLCJwYWdlSWQiOiJBQ0NPVU5UUyIsIm1vYmlsZUF1dGhGbGFnIjoidHJ1ZSIsImN1c3RvbWVyTmFtZSI6InRldGhlcmZpIiwib3JnSWQiOiIxIiwic2tpbGwiOiJBQ0NPVU5UUyIsIm9yZ2FuaXphdGlvbiI6InRldGhlcmZpIiwiY3VzdG9tZXJJZCI6IjU0Njc1MTMwNTEiLCJleHAiOjE4MTcyMDA5NTcsImlhdCI6MTczMDgwMDk1NywianRpIjoiNWJhM2RmNjgtZTkwZC00MDk1LWE0NjAtMGFjMWMwNmIwMjg5In0.DLLXYYoQR_5t6PApBOnK4JtWrq3qQT9dLHV4wTXK9cUIQwzB-wkNukHVbbr6WnhO6eI2yTyFYZw7YT0K_-AeTg";
      let attributes = {};
      if (sessionId != null && sessionId != undefined) {
        attributes['sessionId'] = sessionId;
      }
      if (authType != null && authType != undefined) {
        attributes['authType'] = authType;
      }
      if(enteredCaptchaValue) {
        attributes['requestId'] = this.#LiveChat2Api.api.captchaApi.requestId;
        attributes['enteredCaptcha'] = enteredCaptchaValue;
      }
      this.#LiveChat2Api.setUserAuth(userId, authToken, attributes);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.#setUserAuth] - Error occured while setting user Auth`, ex.message);
    }
  };

  #GenerateRandomId(length) {
    var result = '';
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  #InitializeAvInterface = () => {
    try {
      const avFactory = new window.LiveChatAvFactory();
      this.#avInterface = avFactory?.CreateUsingLivechat2(this.#LiveChat2Api);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.InitializeAvImpl] - Error occured while initializing AV interface`, ex.message);
    }
  };

  CheckDeviceSupport = () => {
    return this.#avInterface.CheckDeviceSupport();
  };

  // this is added for backward compatibility with Visual IVR - LC1 Code
  // AccessToken are not used in LC2 so we are 
  // creating dummy callback 
  GetDummyToken = (data) => { 
    return data.callback(this.#GenerateRandomId(4)); 

  }

  GetAccessToken = function(callback, onerror) {
    this.GetDummyToken(
      {
        callback: function(v) {
            try {
              return callback(v)
            } catch (ex) {
                log(ex);
            }
        },
        errorHandler: function(e) {
            try {
                onerror(e);
            } catch (ex) {
                log(ex);
            }
        }
    });
}

  Open = () => {
    try {
      const sessionExist = this.#LiveChat2Api.open();
      console.log("Session exists", sessionExist);
      // session already exists
      if (sessionExist) {
        // make a request to get the chat history
        console.log('Session already exists');
        this.#LiveChat2Api.getChatHistory();
        // this.logger.info('Session already exists, fetching chat history');
        // this.logger.info('User Id: ' + this.#LiveChat2Api.livechat.getUserId());
        // this.logger.info('Session Id: ' + this.#LiveChat2Api.livechat.getSessionID());
        const sessionData = {
          sid_tchat: this.#LiveChat2Api.livechat.getSessionID(),
          trans: [
            {
              type: 'livechat2',
              id: 'livechat2',
              name: 'livechat2'
            }
          ]
        };
        this.OnOpenSuccess(sessionData);
        // TODO: Check this part
        // this.#LiveChat2Api.getUserInteractionList();
      } else {
        // Similar to OpenFailed-NoSessionFound
        this.OnOpenFailed('NoSessionFound');
      }
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.Open] - Error occured while setting captcha image`, ex.message);
    }
  };

  uploadFiles = (data) => {
    this.logger.info('LiveChat2 does not support uploadFiles method')
  }

  uploadMedia = async (data, org, uploader) => {
    return new Promise(async (resolve, reject) => {
      try {
        this.#LiveChat2Api.uploadMedia(data);
        const fileUploadStatus = await this.#fileUploadStatus();
        resolve(fileUploadStatus);
      } catch (ex) {
        this.logger.error(`[LiveChat2Wrapper.uploadMedia] - Error occured while uploading media`, ex.message);
        reject(ex.message);
      }
    });
  };

  #fileUploadStatus = async () => {
    return new Promise((resolve, reject) => {
      try {
        this.#LiveChat2Api.onFileUploadFailure = (response) =>
          resolve({
            isSuccess: false
          });
        this.#LiveChat2Api.onFileUploadSuccess = (response) =>
          resolve({
            isSuccess: true,
            result: [
              {
                originalName: response?.fileName,
                downloadURL: response?.downloadURL, 
                interactionID: response?.interactionID ?? response?.downloadURL.split('/').pop(),  
                size: NaN
              }
            ]
          });
      } catch (ex) {
        this.logger.error(
          `[LiveChat2Wrapper.#fileUploadStatus] - Error occured while listening for file upload status`,
          ex.message
        );
      }
    });
  };

  OnOpenSuccess = (retCode) => {};
  OnOpenFailed = (retCode) => {};
  OnCallQueued = (queuePos, queueType) => {};

  SetAvConfigs(Config) {
    this.#avInterface.SetAvConfigs(Config);
  }

upgradeToVideo(videoDeviceId = '') {
  try {
    if (videoDeviceId) {
      this.#avInterface.upgradeToVideo(videoDeviceId);
      this.logger.info(`Upgraded to video with device ID: ${videoDeviceId}`);
    } else {
      this.logger.warn('No video device ID provided for upgrade to video.');
    }
  } catch (ex) {
    this.logger.error(`[LiveChat2Wrapper.upgradeToVideo] - Error occurred while upgrading to video`, ex.message);
  }
}


  StartAvCall(sid, uid, config) {
    console.log("Start AV");
    this.#avInterface.StartAvCall(sid, uid, config);
  }

  EndAvCall() {
    this.#avInterface.EndAvCall();
  }

  Pause(audio, video) {
    this.#avInterface.Pause(audio, video, false);
  }

  SetInputDevice(audioId, videoId) {
    this.#avInterface.SetInputDevice(audioId, videoId);
  }

  StartScreensharing(sid, uid, startConfig) {
    this.#avInterface.StartScreensharing(sid, uid, startConfig);
  }

  StopScreensharing() {
    this.#avInterface.StopScreensharing();
  }

  setCaptchaImage = (elRef) => {
    try {
      const captchaUrl = this.#captchaApi.getCaptcha(false);
      elRef.setAttribute('src', captchaUrl);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.setCaptcha] - Error occured while setting captcha image`, ex.message);
    }
  };

  resetCaptchaImage = (elRef) => {
    try {
      const captchaUrl = this.#captchaApi.getCaptcha(true);
      elRef.setAttribute('src', captchaUrl);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.resetCaptchaImage] - Error occured while resetting captcha image`, ex.message);
    }
  };

  Start = (custInfo) => {
    try {
      //if (custInfo?.eEnteredCaptcha) 
      {
        // __channel = this.#startCommandObj.channel;
        this.#setUserAuth(custInfo?.eEnteredCaptcha);
        
        // this.#captchaApi.validateCaptcha(custInfo.eEnteredCaptcha);
      } 
      const params = new URLSearchParams(window.location.search);
      const result = {};
      for (const [key, value] of params.entries()) {
      result[key] = value;
      }
      const userObject = {
        name: this.#startCommandObj.name,
        address: this.#startCommandObj.address,
        pIntent: this.#startCommandObj.pIntent,
        routepath: this.#startCommandObj.routepath,
        pLocation:custInfo.eCustDetails.pLocation,
        pChannel: this.#startCommandObj.channel,        
        chatmode: this.#startCommandObj.chatmode,
        ...result
      };
      // console.log("*********Start Object: ", userObject);
      this.#LiveChat2Api.start(userObject);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.resetCaptchaImage] - Error occured while starting session`, ex.message);
    }
  };

  #SendMessage = (msg) => {
    try {
      this.#LiveChat2Api.sendCustomMessage('cbot-message', msg, 'cbot', this.#SendMessageCallback);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.SendMessage] - Error occured while sending a cbot message`, ex.message);
    }
  };

  SendMessage = (msg) => {
    try {
      let parsedMessage = JSON.parse(msg);
      if (parsedMessage?.attachment) parsedMessage['fileName'] = parsedMessage.attachment.name;
      const sid = this.#LiveChat2Api.livechat.getSessionID();
      this.#userSentMessagesTemp[parsedMessage.messageId] = parsedMessage;
      this.#LiveChat2Api.sendUserMessageV2(
        parsedMessage?.attachment || parsedMessage?.Response ? JSON.stringify(parsedMessage) : msg,
        `interaction:${sid}`,
        this.#SendMessageCallback,
        parsedMessage.messageId
      );
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.SendMessage] - Error occured while sending a message`, ex.message);
    }
  };

  OnEcho = (message, id) => {};

  NotifyTypingStateChange = (state) => {
    try {
      const sid = this.#LiveChat2Api.livechat.getSessionID();
      const stateMapper = {
        1: 'STOP',
        0: 'START'
      };
      this.#LiveChat2Api.sendTypingStateV2(stateMapper[state], `interaction:${sid}`);
    } catch (error) {
      this.logger.error(
        `[LiveChat2Wrapper.NotifyTypingStateChange] - Error occured while notifying typing state`,
        ex.message
      );
    }
  };

  #SendMessageCallback = (mid, bState, mState) => {
    try {
      const stateMapper = { 0: 'new', 1: 'sent', 2: 'nacked', 3: 'acked', 4: 'timedout' };
      if (stateMapper[mState] === 'acked') {
        this.OnEcho(JSON.stringify(this.#userSentMessagesTemp[mid]));
        delete this.#userSentMessagesTemp[mid];
      }
      this.logger.info(`Message send callback ${mid} ${bState} ${mState}`);
    } catch (ex) {
      this.logger.error(`[LiveChat2Wrapper.SendMessageCallback] - Error occured while sending a cbot message`, ex.message);
    }
  };

  GetCaptchaData = async (data) => { 
    // let result = await this.#captchaApi.isCaptchaEnabled();
    let result = await this.#captchaApi.isCaptchaEnabled().then((data1) => {
      return data.callback(data1.captchaEnabled); 
    });
  }

  isCaptchaEnabled = function(callback, onerror) {
    this.GetCaptchaData(
      {
        callback: function(v) {
            try {
              return callback(v)
            } catch (ex) {
                log(ex);
            }
        },
        errorHandler: function(e) {
            try {
                return onerror(e);
            } catch (ex) {
                log(ex);
            }
        }
    });
  }
}
window.LiveChat2Wrapper = LiveChat2Wrapper;
